// Frontend API hooks for the staff onboarding module.
//
// Endpoints (see functions/intranet/src/functions/onboarding.js):
//   POST   /api/onboarding/probearbeiter
//   GET    /api/onboarding/probearbeiter/queue
//   POST   /api/onboarding/probearbeiter/{id}/decide
//   POST   /api/onboarding/draft
//   GET    /api/onboarding/draft/{personMainId}
//   PATCH  /api/onboarding/draft/{personMainId}
//   POST   /api/onboarding/{personMainId}/activate

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../shared/api'
import type { VertragCategory, VertragInput } from '../api/vertragApi'

// ── Types ────────────────────────────────────────────────────────────────

export type PersonStatus = 'pl_pending' | 'onboarding' | 'rejected' | 'paused' | 'active' | 'inactive'
export type IngressKind = 'pl_form' | 'hr_direct'
export type DecisionValue = 'accept' | 'reject'
export type Empfehlung = 'ja' | 'nein' | 'vielleicht'

export interface ProbearbeiterSubmission {
  vorname: string
  nachname: string
  email: string
  handy?: string | null
  probe_schule_id: string
  probe_schule_name?: string | null
  probe_von_datum: string  // ISO yyyy-mm-dd
  probe_bis_datum: string
  probe_stunden_gesamt?: number | null
  probe_rating_punktlichkeit?: number | null
  probe_rating_hygiene?: number | null
  probe_rating_teamfaehigkeit?: number | null
  probe_rating_engagement?: number | null
  probe_empfehlung?: Empfehlung | null
  probe_notizen?: string | null
}

export interface ProbearbeiterRow extends ProbearbeiterSubmission {
  main_id: number
  ingress_by: string | null
  ingress_at: string  // ISO datetime
}

export interface PersonRecord {
  main_id: number
  status: PersonStatus
  ingress: IngressKind
  source: 'intranet'
  personal_id: number | null
  vorname: string | null
  nachname: string | null
  geschlecht: string | null
  geburtsdatum: string | null
  staatsangehoerigkeit: string | null
  familienstand: string | null
  religion: string | null
  email: string | null
  telefonnummer: string | null
  handy: string | null
  strasse: string | null
  plz: string | null
  stadt: string | null
  bundesland: string | null
  firma: string | null
  schule_aktuell: string | null
  steueridentifikationsnummer: string | null
  steuerklasse: number | null
  sozialversicherungsnummer: string | null
  krankenkasse: string | null
  iban: string | null
  arbeitserlaubnis_bis: string | null
  aufenthaltstitel: string | null
  fuehrerschein: string | null
  fuehrerschein_ausstelldatum: string | null
  fuehrerschein_probezeit: string | null
  fuehrungszeugnis: string | null
  gesundheitszeugnis: string | null
  masernimpfung: string | null
  schwerbehindert: string | null
  schwerbehinderungs_prozent: number | null
  geburtsurkunde: string | null
  datenschutzerklaerung: string | null
  einwilligungserklaerung: string | null
  chip_id: string | null
  proficard: string | null
  kiosk_benutzername: string | null
  bankverbindung: string | null
  interne_bemerkungen: string | null
  // Probearbeiter snapshot
  probe_schule_id: string | null
  probe_schule_name: string | null
  probe_von_datum: string | null
  probe_bis_datum: string | null
  probe_rating_punktlichkeit: number | null
  probe_rating_hygiene: number | null
  probe_rating_teamfaehigkeit: number | null
  probe_rating_engagement: number | null
  probe_empfehlung: Empfehlung | null
  probe_notizen: string | null
  // Decision audit
  decision: DecisionValue | null
  decision_at: string | null
  decision_by: string | null
  rejection_reason: string | null
  rejection_email_logged_at: string | null
  // Activation audit
  activated_at: string | null
  activated_by: string | null
}

export interface OnboardingDraftRecord {
  step_no: number
  form_data: Record<string, unknown> | null
  last_section: string | null
  updated_at: string
}

export interface OnboardingState {
  person: PersonRecord
  draft: OnboardingDraftRecord | null
}

// ── Probearbeiter ───────────────────────────────────────────────────────

export function useSubmitProbearbeiter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ProbearbeiterSubmission) =>
      api.post<{ main_id: number; status: PersonStatus }>('/api/onboarding/probearbeiter', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding', 'probequeue'] }),
  })
}

export function useProbearbeiterQueue() {
  return useQuery({
    queryKey: ['onboarding', 'probequeue'],
    queryFn: () => api.get<{ rows: ProbearbeiterRow[] }>('/api/onboarding/probearbeiter/queue'),
    staleTime: 30_000,
  })
}

export function useDecideProbearbeiter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      mainId: number
      decision: DecisionValue
      rejectionReason?: string
      emailWasSent?: boolean
    }) =>
      api.post<{ main_id: number; status: PersonStatus }>(`/api/onboarding/probearbeiter/${vars.mainId}/decide`, {
        decision: vars.decision,
        rejectionReason: vars.rejectionReason,
        emailWasSent: vars.emailWasSent ?? false,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding', 'probequeue'] })
      qc.invalidateQueries({ queryKey: ['onboarding', 'state'] })
    },
  })
}

// ── Wizard / Draft ──────────────────────────────────────────────────────

export function useCreateDraft() {
  return useMutation({
    mutationFn: () => api.post<{ main_id: number; status: PersonStatus }>('/api/onboarding/draft', {}),
  })
}

export function useOnboardingState(personMainId: number | null) {
  return useQuery({
    queryKey: ['onboarding', 'state', personMainId],
    queryFn: () => api.get<OnboardingState>(`/api/onboarding/draft/${personMainId}`),
    enabled: !!personMainId,
    staleTime: 5_000,
  })
}

export function usePatchDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      personMainId: number
      step_no?: number
      last_section?: string
      person?: Partial<PersonRecord>
      form_data?: Record<string, unknown>
    }) =>
      api.patch<{ ok: boolean }>(`/api/onboarding/draft/${vars.personMainId}`, {
        step_no: vars.step_no,
        last_section: vars.last_section,
        person: vars.person,
        form_data: vars.form_data,
      }),
    onSuccess: (_r, vars) => qc.invalidateQueries({ queryKey: ['onboarding', 'state', vars.personMainId] }),
  })
}

// ── Activation ──────────────────────────────────────────────────────────

export interface ActivationRequest {
  vertrag: VertragInput & { category: VertragCategory; firma: string }
}
export interface ActivationResult { main_id: number; status: PersonStatus; personal_id: number }

export function useActivatePerson() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { personMainId: number; payload: ActivationRequest }) =>
      api.post<ActivationResult>(`/api/onboarding/${vars.personMainId}/activate`, vars.payload),
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ['onboarding', 'state', vars.personMainId] })
      qc.invalidateQueries({ queryKey: ['personen'] })
    },
  })
}
